import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import "./RiskDialog.css";

interface RiskData {
  id?: string;
  name: string;
  description: string;
  category: string;
  impact: string;
  likelihood: string;
  severity: string;
  mitigation: string;
  owner: string;
  status: string;
  identifiedDate?: Date | string;
  reviewDate?: Date | string;
}

interface RiskDialogProps {
  visible: boolean;
  riskData?: RiskData;
  setShowRiskDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleSaveRisk: (riskData: RiskData) => void;
  handleDeleteRisk?: () => void;
}

const RiskDialog: React.FC<RiskDialogProps> = ({
  visible,
  riskData,
  setShowRiskDialog,
  handleSaveRisk,
  handleDeleteRisk,
}) => {
  const [name, setName] = useState<string>(riskData?.name || "");
  const [description, setDescription] = useState<string>(
    riskData?.description || ""
  );
  const [category, setCategory] = useState<string>(riskData?.category || "");
  const [impact, setImpact] = useState<string>(riskData?.impact || "Medium");
  const [likelihood, setLikelihood] = useState<string>(
    riskData?.likelihood || "Medium"
  );
  const [severity, setSeverity] = useState<string>(
    riskData?.severity || "Medium"
  );
  const [mitigation, setMitigation] = useState<string>(
    riskData?.mitigation || ""
  );
  const [owner, setOwner] = useState<string>(riskData?.owner || "");
  const [status, setStatus] = useState<string>(riskData?.status || "Open");
  const [identifiedDate, setIdentifiedDate] = useState<Date | undefined>(
    riskData?.identifiedDate ? new Date(riskData.identifiedDate) : undefined
  );
  const [reviewDate, setReviewDate] = useState<Date | undefined>(
    riskData?.reviewDate ? new Date(riskData.reviewDate) : undefined
  );

  useEffect(() => {
    setName(riskData?.name || "");
    setDescription(riskData?.description || "");
    setCategory(riskData?.category || "");
    setImpact(riskData?.impact || "Medium");
    setLikelihood(riskData?.likelihood || "Medium");
    setSeverity(riskData?.severity || "Medium");
    setMitigation(riskData?.mitigation || "");
    setOwner(riskData?.owner || "");
    setStatus(riskData?.status || "Open");
    setIdentifiedDate(
      riskData?.identifiedDate ? new Date(riskData.identifiedDate) : undefined
    );
    setReviewDate(
      riskData?.reviewDate ? new Date(riskData.reviewDate) : undefined
    );
  }, [riskData, visible]);

  // Auto-calculate severity based on impact and likelihood
  useEffect(() => {
    const calculateSeverity = () => {
      if (impact === "High" && likelihood === "High") return "Critical";
      if (
        (impact === "High" && likelihood === "Medium") ||
        (impact === "Medium" && likelihood === "High")
      )
        return "High";
      if (
        impact === "Low" ||
        likelihood === "Low" ||
        (impact === "Medium" && likelihood === "Medium")
      )
        return "Medium";
      return "Low";
    };
    setSeverity(calculateSeverity());
  }, [impact, likelihood]);

  const categoryOptions = [
    { label: "Technical", value: "Technical" },
    { label: "Schedule", value: "Schedule" },
    { label: "Budget", value: "Budget" },
    { label: "Resource", value: "Resource" },
    { label: "Quality", value: "Quality" },
    { label: "Security", value: "Security" },
    { label: "Compliance", value: "Compliance" },
    { label: "Operational", value: "Operational" },
    { label: "Strategic", value: "Strategic" },
    { label: "External", value: "External" },
    { label: "Other", value: "Other" },
  ];

  const impactOptions = [
    { label: "High", value: "High" },
    { label: "Medium", value: "Medium" },
    { label: "Low", value: "Low" },
  ];

  const likelihoodOptions = [
    { label: "High", value: "High" },
    { label: "Medium", value: "Medium" },
    { label: "Low", value: "Low" },
  ];

  const severityOptions = [
    { label: "Critical", value: "Critical" },
    { label: "High", value: "High" },
    { label: "Medium", value: "Medium" },
    { label: "Low", value: "Low" },
  ];

  const statusOptions = [
    { label: "Open", value: "Open" },
    { label: "Mitigating", value: "Mitigating" },
    { label: "Closed", value: "Closed" },
    { label: "Accepted", value: "Accepted" },
  ];

  const handleSave = () => {
    // Validate required fields
    if (!name || name.trim() === "") {
      alert("Risk name is required. Please enter a risk name.");
      return;
    }

    handleSaveRisk({
      id: riskData?.id,
      name: name.trim(),
      description,
      category,
      impact,
      likelihood,
      severity,
      mitigation,
      owner,
      status,
      identifiedDate,
      reviewDate,
    });
    setShowRiskDialog(false);
  };

  return (
    <Dialog
      visible={visible}
      modal
      header="Risk Details"
      className="risk-dialog"
      style={{ width: "700px", maxWidth: "90vw" }}
      onHide={() => setShowRiskDialog(false)}
      footer={
        <div>
          {riskData?.id && handleDeleteRisk && (
            <Button
              label="Delete"
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              onClick={handleDeleteRisk}
            />
          )}
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setShowRiskDialog(false)}
          />
          <Button
            label="Save"
            icon="pi pi-check"
            className="p-button-primary"
            onClick={handleSave}
          />
        </div>
      }
    >
      <div className="risk-dialog-form">
        {/* Name */}
        <div className="risk-dialog-field">
          <label htmlFor="riskName">
            Risk Name <span className="required">*</span>
          </label>
          <InputText
            id="riskName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter risk name"
          />
        </div>

        {/* Description */}
        <div className="risk-dialog-field">
          <label htmlFor="riskDescription">Description</label>
          <InputTextarea
            id="riskDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the risk in detail"
            rows={3}
          />
        </div>

        <div className="risk-dialog-row">
          {/* Category */}
          <div className="risk-dialog-field">
            <label htmlFor="riskCategory">Category</label>
            <Dropdown
              id="riskCategory"
              value={category}
              options={categoryOptions}
              onChange={(e) => setCategory(e.value)}
              placeholder="Select category"
              editable
            />
          </div>

          {/* Status */}
          <div className="risk-dialog-field">
            <label htmlFor="riskStatus">Status</label>
            <Dropdown
              id="riskStatus"
              value={status}
              options={statusOptions}
              onChange={(e) => setStatus(e.value)}
              placeholder="Select status"
            />
          </div>
        </div>

        <div className="risk-dialog-row">
          {/* Impact */}
          <div className="risk-dialog-field">
            <label htmlFor="riskImpact">Impact</label>
            <Dropdown
              id="riskImpact"
              value={impact}
              options={impactOptions}
              onChange={(e) => setImpact(e.value)}
              placeholder="Select impact"
            />
          </div>

          {/* Likelihood */}
          <div className="risk-dialog-field">
            <label htmlFor="riskLikelihood">Likelihood</label>
            <Dropdown
              id="riskLikelihood"
              value={likelihood}
              options={likelihoodOptions}
              onChange={(e) => setLikelihood(e.value)}
              placeholder="Select likelihood"
            />
          </div>
        </div>

        {/* Severity (auto-calculated) */}
        <div className="risk-dialog-field">
          <label htmlFor="riskSeverity">Severity (Auto-calculated)</label>
          <Dropdown
            id="riskSeverity"
            value={severity}
            options={severityOptions}
            onChange={(e) => setSeverity(e.value)}
            placeholder="Severity"
            disabled
          />
        </div>

        {/* Mitigation */}
        <div className="risk-dialog-field">
          <label htmlFor="riskMitigation">Mitigation Plan</label>
          <InputTextarea
            id="riskMitigation"
            value={mitigation}
            onChange={(e) => setMitigation(e.target.value)}
            placeholder="Describe mitigation strategies"
            rows={3}
          />
        </div>

        {/* Owner */}
        <div className="risk-dialog-field">
          <label htmlFor="riskOwner">Risk Owner</label>
          <InputText
            id="riskOwner"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Enter risk owner name"
          />
        </div>

        <div className="risk-dialog-row">
          {/* Identified Date */}
          <div className="risk-dialog-field">
            <label htmlFor="riskIdentifiedDate">Identified Date</label>
            <Calendar
              id="riskIdentifiedDate"
              value={identifiedDate}
              onChange={(e) => setIdentifiedDate(e.value as Date)}
              placeholder="Select date"
              dateFormat="yy-mm-dd"
              showIcon
            />
          </div>

          {/* Review Date */}
          <div className="risk-dialog-field">
            <label htmlFor="riskReviewDate">Review Date</label>
            <Calendar
              id="riskReviewDate"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.value as Date)}
              placeholder="Select date"
              dateFormat="yy-mm-dd"
              showIcon
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RiskDialog;
